import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Flame,
  Clock,
  Zap,
  CheckCircle2,
  Hourglass,
  AlertTriangle,
  Swords,
  Rocket,
  Skull,
  Ghost,
  Flag,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// The six built-in bill-ranking categories + the sports-style personas that
// pre-select them. Categories map 1:1 to backend `/api/bills/rankings/{cat}`.
const BILL_CATEGORIES = [
  { id: "critical", label: "Most Critical", icon: AlertTriangle, tone: "text-rose-400", blurb: "Must-pass bills, shutdown risk, debt ceiling, NDAA, appropriations" },
  { id: "contentious", label: "Most Contentious", icon: Swords, tone: "text-orange-400", blurb: "Ideologically charged legislation driving the loudest floor debates" },
  { id: "stalled", label: "Most Stalled", icon: Ghost, tone: "text-slate-400", blurb: "Bills idle for the longest, slowly being forgotten" },
  { id: "recent", label: "Most Recent Activity", icon: Zap, tone: "text-yellow-400", blurb: "Freshly moving, just introduced, just voted, just reported" },
  { id: "active", label: "Active & Moving", icon: Rocket, tone: "text-sky-400", blurb: "In-progress bills advancing through the pipeline right now" },
  { id: "enacted", label: "Recently Enacted", icon: CheckCircle2, tone: "text-emerald-400", blurb: "Signed into law, finished the journey" },
];

// Bill personas: one-click archetypes that map to a single category.
// (Unlike legislator personas these are single-category since there's no
// composite scoring yet for bills, future iter could blend cosponsor data.)
const BILL_PERSONAS = [
  { id: "must-pass", label: "The Must-Pass", category: "critical", icon: Flag, tint: "from-rose-500/20 to-orange-500/10 border-rose-500/30", blurb: "Government-funding & national-defense bills Congress cannot ignore" },
  { id: "lightning-rod", label: "The Lightning Rod", category: "contentious", icon: Flame, tint: "from-orange-500/20 to-yellow-500/10 border-orange-500/30", blurb: "Bills that split the country down the middle" },
  { id: "zombie", label: "The Zombie", category: "stalled", icon: Skull, tint: "from-slate-500/20 to-zinc-500/10 border-slate-500/30", blurb: "Dead-but-not-buried, stuck in committee for months" },
  { id: "sprinter", label: "The Sprinter", category: "recent", icon: Zap, tint: "from-yellow-500/20 to-amber-500/10 border-yellow-500/30", blurb: "Bills racing through the process in the past week" },
  { id: "on-the-move", label: "On the Move", category: "active", icon: Rocket, tint: "from-sky-500/20 to-blue-500/10 border-sky-500/30", blurb: "In-progress legislation making real headway" },
  { id: "finish-line", label: "Finish Line", category: "enacted", icon: CheckCircle2, tint: "from-emerald-500/20 to-teal-500/10 border-emerald-500/30", blurb: "Recently signed into law" },
];

// Map status → colored pill
const statusChip = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "passed") return { bg: "bg-emerald-500/20 text-emerald-300 border-emerald-400/40", label: "Enacted" };
  if (s === "failed") return { bg: "bg-rose-500/20 text-rose-300 border-rose-400/40", label: "Failed" };
  return { bg: "bg-sky-500/15 text-sky-300 border-sky-400/30", label: "In Progress" };
};

const LegislationRankings = ({ congressNum }) => {
  const [category, setCategory] = useState("critical");
  const [chamber, setChamber] = useState("all");
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (chamber !== "all") params.set("chamber", chamber);
        if (congressNum && congressNum !== 119) params.set("congress", String(congressNum));
        params.set("limit", "50");
        const url = `${API}/bills/rankings/${category}?${params.toString()}`;
        const res = await axios.get(url);
        if (!cancelled) setBills(res.data || []);
      } catch (e) {
        if (!cancelled) setBills([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [category, chamber, congressNum]);

  const activeCat = BILL_CATEGORIES.find((c) => c.id === category);

  return (
    <div className="space-y-6" data-testid="legislation-rankings">
      {/* Bill Personas row */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-white/80 block">
          Bill Personas
          <span className="text-xs text-white/45 ml-2">
            One-click archetypes, each jumps you to a curated slice of the legislation
          </span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
          {BILL_PERSONAS.map((p) => {
            const Icon = p.icon;
            const active = category === p.category;
            return (
              <button
                key={p.id}
                type="button"
                data-testid={`bill-persona-${p.id}`}
                onClick={() => setCategory(p.category)}
                title={p.blurb}
                className={`relative text-left rounded-lg border p-3 bg-gradient-to-br transition-all ${p.tint} ${
                  active
                    ? "ring-2 ring-orange-400 shadow-[0_0_24px_rgba(255,138,0,0.25)]"
                    : "hover:border-white/30"
                }`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Icon className="w-4 h-4 text-white/90" />
                  <span className="text-[13px] font-bold text-white tracking-tight leading-tight">{p.label}</span>
                </div>
                <p className="text-[11px] text-white/60 leading-snug line-clamp-2">{p.blurb}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Category chips */}
      <div>
        <label className="text-sm font-medium text-white/80 block mb-2">Category</label>
        <div className="flex flex-wrap gap-2" data-testid="bill-categories">
          {BILL_CATEGORIES.map(({ id, label, icon: Icon, tone }) => {
            const active = category === id;
            return (
              <button
                key={id}
                type="button"
                data-testid={`bill-cat-${id}`}
                onClick={() => setCategory(id)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border transition-all ${
                  active
                    ? "whip-orange-bg text-white border-orange-400 shadow-[0_0_0_1px_rgba(255,138,0,0.4)]"
                    : "bg-white/5 text-white/65 border-white/15 hover:text-white hover:border-white/30"
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${active ? "text-white" : tone}`} />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Chamber row */}
      <div>
        <label className="text-sm font-medium text-white/80 block mb-2">Chamber</label>
        <div className="inline-flex gap-2">
          {[
            { id: "all", label: "All" },
            { id: "House", label: "House" },
            { id: "Senate", label: "Senate" },
          ].map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid={`bill-chamber-${c.id.toLowerCase()}`}
              onClick={() => setChamber(c.id)}
              className={`text-xs font-semibold px-3 py-2 rounded-md border transition-all ${
                chamber === c.id
                  ? "bg-white/15 text-white border-white/30"
                  : "bg-white/5 text-white/65 border-white/15 hover:text-white hover:border-white/30"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Header + subtitle */}
      <div className="pt-2">
        <h2 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
          {activeCat?.icon && (
            <activeCat.icon className={`w-6 h-6 ${activeCat.tone}`} />
          )}
          {activeCat?.label}
        </h2>
        <p className="text-sm text-white/55 mt-1">{activeCat?.blurb}</p>
      </div>

      {/* Bills list */}
      {loading && (
        <div className="space-y-2" data-testid="bill-rank-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-20 rounded-lg animate-pulse" style={{ background: "var(--whip-surface)" }} />
          ))}
        </div>
      )}

      {!loading && bills.length === 0 && (
        <Card className="p-10 text-center rounded-xl" style={{ background: "var(--whip-surface)" }} data-testid="bill-rank-empty">
          <Hourglass className="w-10 h-10 text-white/30 mx-auto mb-3" />
          <p className="text-white/70 font-medium">No bills match this category right now</p>
          <p className="text-xs text-white/45 mt-1">
            {category === "enacted"
              ? "Recently-enacted bills will appear here as they become law. Check back after the next session."
              : "Try switching chamber or pick a different persona."}
          </p>
        </Card>
      )}

      {!loading && bills.length > 0 && (
        <div className="space-y-2">
          {bills.map((b, idx) => {
            const chip = statusChip(b.status);
            const meta = b._rankMeta || {};
            const sponsor = b.sponsor || {};
            return (
              <Link key={b.id} to={`/bill/${b.id}`} className="block">
              <Card
                data-testid={`bill-rank-card-${idx + 1}`}
                className="p-4 rounded-lg cursor-pointer border border-white/10 hover:border-orange-400/50 transition-all group"
                style={{ background: "var(--whip-surface)" }}
              >
                <div className="flex items-start gap-4">
                  {/* Rank number */}
                  <div className="flex flex-col items-center justify-center pt-1">
                    <span className={`text-2xl font-black leading-none ${idx < 3 ? "text-orange-400" : "text-white/40"}`}>
                      {idx + 1}
                    </span>
                  </div>

                  {/* Main body */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <Badge className="bg-white/10 text-white border-white/20 text-[10px] font-mono tracking-wide">
                        {b.billNumber}
                      </Badge>
                      <Badge className={`${chip.bg} text-[10px] font-semibold border`}>{chip.label}</Badge>
                      <Badge className="bg-white/5 text-white/60 text-[10px] border-white/15">
                        {b.chamber}
                      </Badge>
                      {meta.matchedTerms && meta.matchedTerms.length > 0 && (
                        <div className="flex items-center gap-1 flex-wrap">
                          {meta.matchedTerms.slice(0, 3).map((t) => (
                            <span
                              key={t}
                              className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-300 border border-orange-500/25"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-white group-hover:text-orange-200 transition-colors line-clamp-2">
                      {b.title || "(untitled bill)"}
                    </h3>
                    <div className="flex items-center gap-3 mt-1.5 text-[11px] text-white/50">
                      {sponsor.name && (
                        <span className="truncate max-w-[220px]">
                          Sponsored by <span className="text-white/70">{sponsor.name}</span>
                          {sponsor.party && sponsor.state ? ` (${sponsor.party}-${sponsor.state})` : ""}
                        </span>
                      )}
                      {meta.blurb && (
                        <span className="flex items-center gap-1 text-white/55">
                          <Clock className="w-3 h-3" />
                          {meta.blurb}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Score pill */}
                  <div className="flex flex-col items-end justify-center flex-shrink-0">
                    <div className="text-[10px] text-white/45 uppercase tracking-wider">
                      {category === "stalled"
                        ? "Days Idle"
                        : category === "recent" || category === "active"
                        ? "Days Ago"
                        : "Score"}
                    </div>
                    <div className="text-lg font-bold text-white">
                      {category === "recent" || category === "active"
                        ? meta.daysSinceLastAction ?? "n/a"
                        : category === "stalled"
                        ? meta.daysSinceLastAction ?? "n/a"
                        : meta.score ?? "n/a"}
                    </div>
                  </div>
                </div>
              </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LegislationRankings;
export { BILL_CATEGORIES, BILL_PERSONAS };
